/**
 * Environment detection utility
 * Provides centralized environment checking to replace scattered typeof checks
 * Updated to use standardized error handling
 */

import { ConfigurationError } from './error-handling';

interface EnvironmentInfo {
    isBrowser: boolean;
    isNode: boolean;
    isWebWorker: boolean;
    isTest: boolean;
    isDevelopment: boolean;
    isProduction: boolean;
    browserFeatures?: {
        webAudio: boolean;
        localStorage: boolean;
        canvas: boolean;
        fileAPI: boolean;
    };
    userAgent?: string;
    location?: {
        hostname: string;
        protocol: string;
    };
    nodeVersion?: string;
    platform?: string;
}

interface FeatureMap {
    webAudio: boolean;
    localStorage: boolean;
    canvas: boolean;
    fileAPI: boolean;
}

// Safe process access for browser environment
const hasProcess = (() => {
  try {
    return typeof globalThis !== 'undefined' && 
           (globalThis as any).process;
  } catch {
    return false;
  }
})();

const Environment = {
    // Environment flags
    isBrowser: typeof window !== 'undefined' && typeof document !== 'undefined',
    isNode: hasProcess && hasProcess.versions && !!hasProcess.versions.node,
    isWebWorker: typeof window !== 'undefined' && typeof (window as any).importScripts === 'function' && typeof navigator !== 'undefined',
    isTest: hasProcess && hasProcess.env && hasProcess.env.NODE_ENV === 'test',
    
    // Browser feature detection
    hasWebAudio: typeof window !== 'undefined' && ((window as any).AudioContext || (window as any).webkitAudioContext),
    hasLocalStorage: typeof window !== 'undefined' && typeof window.localStorage !== 'undefined',
    hasCanvas: typeof window !== 'undefined' && typeof window.HTMLCanvasElement !== 'undefined',
    hasFileAPI: typeof window !== 'undefined' && window.File && window.FileReader && window.FileList && window.Blob,
    
    /**
     * Require browser environment
     * @param {string} feature - Feature name for error message
     * @throws {ConfigurationError} If not in browser environment
     */
    requireBrowser(feature: string = 'This feature'): void {
        if (!this.isBrowser) {
            throw new ConfigurationError('Environment', 'requireBrowser', `${feature} requires a browser environment`);
        }
    },
    
    /**
     * Require Node.js environment
     * @param {string} feature - Feature name for error message
     * @throws {ConfigurationError} If not in Node.js environment
     */
    requireNode(feature: string = 'This feature'): void {
        if (!this.isNode) {
            throw new ConfigurationError('Environment', 'requireNode', `${feature} requires a Node.js environment`);
        }
    },
    
    /**
     * Require specific browser feature
     * @param {string} featureName - Name of the feature to check
     * @param {string} customMessage - Custom error message
     * @throws {ConfigurationError} If feature is not available
     */
    requireFeature(featureName: keyof FeatureMap, customMessage: string | null = null): void {
        this.requireBrowser();
        
        const features: FeatureMap = {
            webAudio: this.hasWebAudio,
            localStorage: this.hasLocalStorage,
            canvas: this.hasCanvas,
            fileAPI: this.hasFileAPI
        };
        
        if (features[featureName] === undefined) {
            throw new ConfigurationError('Environment', 'requireFeature', `Unknown feature: ${featureName}`);
        }
        
        if (!features[featureName]) {
            const message = customMessage || `${featureName} is not available in this browser`;
            throw new ConfigurationError('Environment', 'requireFeature', message);
        }
    },
    
    /**
     * Check if running in development mode
     * @returns {boolean} True if in development mode
     */
    isDevelopment(): boolean {
        if (this.isNode) {
            return hasProcess && hasProcess.env && hasProcess.env.NODE_ENV === 'development';
        }
        if (this.isBrowser) {
            return window.location.hostname === 'localhost' || 
                   window.location.hostname === '127.0.0.1' ||
                   window.location.protocol === 'file:';
        }
        return false;
    },
    
    /**
     * Check if running in production mode
     * @returns {boolean} True if in production mode
     */
    isProduction(): boolean {
        if (this.isNode) {
            return hasProcess && hasProcess.env && hasProcess.env.NODE_ENV === 'production';
        }
        return !this.isDevelopment();
    },
    
    /**
     * Get environment info for debugging
     * @returns {Object} Environment information
     */
    getInfo(): EnvironmentInfo {
        const info: EnvironmentInfo = {
            isBrowser: this.isBrowser,
            isNode: this.isNode,
            isWebWorker: this.isWebWorker,
            isTest: this.isTest,
            isDevelopment: this.isDevelopment(),
            isProduction: this.isProduction()
        };
        
        if (this.isBrowser) {
            info.browserFeatures = {
                webAudio: this.hasWebAudio,
                localStorage: this.hasLocalStorage,
                canvas: this.hasCanvas,
                fileAPI: this.hasFileAPI
            };
            
            info.userAgent = navigator.userAgent;
            info.location = {
                hostname: window.location.hostname,
                protocol: window.location.protocol
            };
        }
        
        if (this.isNode) {
            info.nodeVersion = hasProcess ? hasProcess.version : undefined;
            info.platform = hasProcess ? hasProcess.platform : undefined;
        }
        
        return info;
    }
};

// ES module export
export { Environment };